import { useMemo, useRef, useState } from 'react';
import { Image as ImageIcon, Plus, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  EditableCategory,
  EditableSelect,
  EditableText,
} from '@/components/inventory/EditableItemField';
import { EditableKV, SummaryCard } from '@/components/equipment/DetailRows';
import {
  DueDate,
  EquipmentConditionBadge,
  EquipmentStatusBadge,
  WarrantyBadge,
} from '@/components/equipment/EquipmentBadges';
import { useLocations } from '@/features/inventory/hooks';
import { useProfiles } from '@/features/users/hooks';
import {
  equipmentSchedule,
  useEquipmentFileUrl,
  useEquipmentList,
  useMaintenancePlans,
  useUpdateEquipment,
  useUploadEquipmentFile,
  type Equipment,
  type EquipmentPatch,
  type EquipmentSpec,
} from '@/features/equipment/hooks';
import { depreciationAsOf } from '@/features/equipment/depreciation';
import {
  EQUIPMENT_CATEGORY_META,
  EQUIPMENT_CATEGORY_OPTIONS,
  EQUIPMENT_CONDITION_OPTIONS,
  EQUIPMENT_STATUS_OPTIONS,
  MAX_EQUIPMENT_PHOTO_BYTES,
  equipmentCategoryLabel,
  isEquipmentCategory,
} from '@/features/equipment/model';
import { formatCurrency, formatDateOnly, todayIsoDate } from '@/lib/formatters';

const NONE = '__none';

export function EquipmentOverviewTab({
  equipment,
  canEdit,
  onOpenTab,
}: {
  equipment: Equipment;
  canEdit: boolean;
  onOpenTab: (tab: string) => void;
}) {
  return (
    <div className="space-y-6">
      <AtAGlance equipment={equipment} onOpenTab={onOpenTab} />
      <div className="grid gap-6 lg:grid-cols-3">
        <CoverPhotoCard equipment={equipment} canEdit={canEdit} onOpenTab={onOpenTab} />
        <DetailsCard equipment={equipment} canEdit={canEdit} className="lg:col-span-2" />
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <SpecsCard equipment={equipment} canEdit={canEdit} />
        <NotesCard equipment={equipment} canEdit={canEdit} />
      </div>
    </div>
  );
}

function AtAGlance({
  equipment,
  onOpenTab,
}: {
  equipment: Equipment;
  onOpenTab: (tab: string) => void;
}) {
  const { data: plans } = useMaintenancePlans(equipment.id);
  const position = useMemo(
    () => depreciationAsOf(equipmentSchedule(equipment), todayIsoDate()),
    [equipment],
  );
  const nextDue = (plans ?? [])
    .filter((p) => p.is_active && p.next_due_on)
    .map((p) => p.next_due_on as string)
    .sort()[0];
  const inService = equipment.in_service_on ?? equipment.acquired_at;

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <SummaryCard
        label="Book value today"
        value={position ? formatCurrency(position.bookValueCents, { whole: true }) : '—'}
        caption={
          equipment.purchase_price_cents != null
            ? `of ${formatCurrency(equipment.purchase_price_cents, { whole: true })} cost`
            : 'Add a purchase price'
        }
        onClick={() => onOpenTab('depreciation')}
      />
      <SummaryCard
        label="Next maintenance"
        value={nextDue ? <DueDate date={nextDue} className="flex-wrap text-xl" /> : '—'}
        caption={nextDue ? undefined : 'No schedule yet'}
        onClick={() => onOpenTab('maintenance')}
      />
      <SummaryCard
        label="Warranty"
        value={
          equipment.warranty_expires_on ? (
            <WarrantyBadge expiresOn={equipment.warranty_expires_on} />
          ) : (
            '—'
          )
        }
        caption={equipment.warranty_provider ?? undefined}
        onClick={() => onOpenTab('purchase')}
      />
      <SummaryCard
        label="In service since"
        value={inService ? formatDateOnly(inService) : '—'}
        caption={inService ? ageLabel(inService) : undefined}
      />
    </div>
  );
}

function ageLabel(since: string): string {
  const [y, m] = since.split('-').map(Number);
  const now = new Date();
  const months = (now.getFullYear() - y) * 12 + (now.getMonth() + 1 - m);
  if (months < 1) return 'Less than a month';
  if (months < 12) return `${months} month${months === 1 ? '' : 's'}`;
  const years = Math.floor(months / 12);
  const rest = months % 12;
  return rest
    ? `${years} yr ${rest} mo`
    : `${years} year${years === 1 ? '' : 's'}`;
}

function CoverPhotoCard({
  equipment,
  canEdit,
  onOpenTab,
}: {
  equipment: Equipment;
  canEdit: boolean;
  onOpenTab: (tab: string) => void;
}) {
  const { data: url, isLoading } = useEquipmentFileUrl(equipment.image_path);
  const upload = useUploadEquipmentFile();
  const inputRef = useRef<HTMLInputElement>(null);

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Pick an image file (PNG, JPG, WEBP, etc.).');
      return;
    }
    if (file.size > MAX_EQUIPMENT_PHOTO_BYTES) {
      toast.error('Photos must be under 15 MB.');
      return;
    }
    try {
      await upload.mutateAsync({
        equipmentId: equipment.id,
        file,
        kind: 'photo',
        makeCover: true,
      });
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Photo</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <button
          type="button"
          onClick={() => onOpenTab('photos')}
          className="relative block aspect-square w-full overflow-hidden rounded-md border border-border bg-secondary/30"
          aria-label="Open photos"
        >
          {equipment.image_path ? (
            isLoading || !url ? (
              <Skeleton className="h-full w-full" />
            ) : (
              <img src={url} alt={equipment.name} className="h-full w-full object-contain" />
            )
          ) : (
            <span className="flex h-full w-full flex-col items-center justify-center gap-2 text-muted-foreground">
              <ImageIcon className="h-10 w-10" />
              <span className="text-xs">No photo yet</span>
            </span>
          )}
        </button>
        {canEdit && (
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => void onFile(e)}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => inputRef.current?.click()}
              disabled={upload.isPending}
            >
              <Upload className="h-4 w-4" />
              {upload.isPending
                ? 'Uploading…'
                : equipment.image_path
                  ? 'Replace cover'
                  : 'Upload photo'}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => onOpenTab('photos')}>
              All photos
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DetailsCard({
  equipment: e,
  canEdit,
  className,
}: {
  equipment: Equipment;
  canEdit: boolean;
  className?: string;
}) {
  const update = useUpdateEquipment();
  const { data: peers } = useEquipmentList();
  const { data: locations } = useLocations({ includeInactive: true });
  const { data: profiles } = useProfiles();

  const save = async (patch: EquipmentPatch) => {
    await update.mutateAsync({ id: e.id, patch });
  };

  const typeOptions = useMemo(() => {
    const seen = new Set<string>(
      isEquipmentCategory(e.category) ? EQUIPMENT_CATEGORY_META[e.category].typeSuggestions : [],
    );
    for (const p of peers ?? []) {
      if (p.category === e.category && p.equipment_type) seen.add(p.equipment_type);
    }
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [peers, e.category]);

  const manufacturerOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const p of peers ?? []) if (p.manufacturer) seen.add(p.manufacturer);
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [peers]);

  const locationOptions = useMemo(
    () => [
      { value: NONE, label: 'No location' },
      ...(locations ?? [])
        .filter((l) => l.is_active || l.id === e.location_id)
        .map((l) => ({ value: l.id, label: l.name, hint: l.code })),
    ],
    [locations, e.location_id],
  );

  const assigneeOptions = useMemo(
    () => [
      { value: NONE, label: 'Unassigned' },
      ...(profiles ?? [])
        .filter((p) => p.is_active || p.id === e.assigned_to)
        .map((p) => ({ value: p.id, label: p.full_name ?? p.email })),
    ],
    [profiles, e.assigned_to],
  );

  const conditionOptions = useMemo(
    () => [{ value: NONE, label: 'Not set' }, ...EQUIPMENT_CONDITION_OPTIONS],
    [],
  );

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>Equipment details</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        <EditableKV
          label="Asset tag"
          editor={
            <EditableText
              value={e.asset_tag}
              variant="mono"
              required
              maxLength={64}
              ariaLabel="Asset tag"
              canEdit={canEdit}
              density="kv"
              align="right"
              onSave={(next) => save({ asset_tag: next })}
            />
          }
        />
        <EditableKV
          label="Name"
          editor={
            <EditableText
              value={e.name}
              required
              maxLength={200}
              ariaLabel="Name"
              canEdit={canEdit}
              density="kv"
              align="right"
              onSave={(next) => save({ name: next })}
            />
          }
        />
        <EditableKV
          label="Category"
          editor={
            <EditableSelect
              value={e.category}
              options={EQUIPMENT_CATEGORY_OPTIONS}
              ariaLabel="Category"
              canEdit={canEdit}
              density="kv"
              align="right"
              display={<span>{equipmentCategoryLabel(e.category)}</span>}
              onSave={(next) => save({ category: next })}
            />
          }
        />
        <EditableKV
          label="Type"
          editor={
            <EditableCategory
              value={e.equipment_type}
              options={typeOptions}
              ariaLabel="Type"
              canEdit={canEdit}
              density="kv"
              align="right"
              placeholder="e.g. CNC mill"
              display={e.equipment_type ? <span>{e.equipment_type}</span> : undefined}
              onSave={(next) => save({ equipment_type: next })}
            />
          }
        />
        <EditableKV
          label="Manufacturer"
          editor={
            <EditableCategory
              value={e.manufacturer}
              options={manufacturerOptions}
              ariaLabel="Manufacturer"
              canEdit={canEdit}
              density="kv"
              align="right"
              placeholder="—"
              display={e.manufacturer ? <span>{e.manufacturer}</span> : undefined}
              onSave={(next) => save({ manufacturer: next })}
            />
          }
        />
        <EditableKV
          label="Model"
          editor={
            <EditableText
              value={e.model ?? ''}
              maxLength={120}
              ariaLabel="Model"
              canEdit={canEdit}
              density="kv"
              align="right"
              placeholder="—"
              onSave={(next) => save({ model: next })}
            />
          }
        />
        <EditableKV
          label="Serial number"
          editor={
            <EditableText
              value={e.serial_number ?? ''}
              variant="mono"
              maxLength={120}
              ariaLabel="Serial number"
              canEdit={canEdit}
              density="kv"
              align="right"
              placeholder="—"
              onSave={(next) => save({ serial_number: next })}
            />
          }
        />
        <EditableKV
          label="Status"
          editor={
            <EditableSelect
              value={e.status}
              options={EQUIPMENT_STATUS_OPTIONS}
              ariaLabel="Status"
              canEdit={canEdit}
              density="kv"
              align="right"
              display={<EquipmentStatusBadge status={e.status} />}
              onSave={(next) => save({ status: next })}
            />
          }
        />
        <EditableKV
          label="Condition"
          editor={
            <EditableSelect
              value={e.condition ?? NONE}
              options={conditionOptions}
              ariaLabel="Condition"
              canEdit={canEdit}
              density="kv"
              align="right"
              display={<EquipmentConditionBadge condition={e.condition} />}
              onSave={(next) => save({ condition: next === NONE ? null : next })}
            />
          }
        />
        <EditableKV
          label="Location"
          editor={
            <EditableSelect
              value={e.location_id ?? NONE}
              options={locationOptions}
              ariaLabel="Location"
              canEdit={canEdit}
              density="kv"
              align="right"
              display={
                e.location ? (
                  <span>{e.location.name}</span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )
              }
              onSave={(next) => save({ location_id: next === NONE ? null : next })}
            />
          }
        />
        <EditableKV
          label="Spot"
          editor={
            <EditableText
              value={e.location_detail ?? ''}
              maxLength={120}
              ariaLabel="Spot"
              canEdit={canEdit}
              density="kv"
              align="right"
              placeholder="e.g. Bay 3, north wall"
              onSave={(next) => save({ location_detail: next })}
            />
          }
        />
        <EditableKV
          label="Assigned to"
          editor={
            <EditableSelect
              value={e.assigned_to ?? NONE}
              options={assigneeOptions}
              ariaLabel="Assigned to"
              canEdit={canEdit}
              density="kv"
              align="right"
              display={
                e.assignee ? (
                  <span>{e.assignee.full_name ?? e.assignee.email}</span>
                ) : (
                  <span className="text-muted-foreground">Unassigned</span>
                )
              }
              onSave={(next) => save({ assigned_to: next === NONE ? null : next })}
            />
          }
        />
        <div className="space-y-1 pt-2 sm:col-span-2">
          <span className="text-muted-foreground">Description</span>
          <EditableText
            value={e.description ?? ''}
            multiline
            rows={4}
            maxLength={4000}
            ariaLabel="Description"
            canEdit={canEdit}
            density="kv"
            placeholder="What it is, what it's used for, anything unusual about it."
            onSave={(next) => save({ description: next })}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function SpecsCard({ equipment, canEdit }: { equipment: Equipment; canEdit: boolean }) {
  const update = useUpdateEquipment();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<EquipmentSpec[]>([]);
  const suggestions = isEquipmentCategory(equipment.category)
    ? EQUIPMENT_CATEGORY_META[equipment.category].specSuggestions
    : [];
  const datalistId = `equipment-spec-labels-${equipment.id}`;

  const start = () => {
    setDraft(equipment.specs.length ? equipment.specs.map((s) => ({ ...s })) : [{ label: '', value: '' }]);
    setEditing(true);
  };

  const saveSpecs = async () => {
    const cleaned = draft
      .map((s) => ({ label: s.label.trim(), value: s.value.trim() }))
      .filter((s) => s.label || s.value);
    if (cleaned.some((s) => !s.label)) {
      toast.error('Every spec needs a name.');
      return;
    }
    try {
      await update.mutateAsync({ id: equipment.id, patch: { specs: cleaned } });
      setEditing(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save specs.');
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle>Specifications</CardTitle>
          {canEdit && !editing && (
            <Button variant="outline" size="sm" onClick={start}>
              {equipment.specs.length ? 'Edit' : 'Add specs'}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {editing ? (
          <div className="space-y-2">
            <datalist id={datalistId}>
              {suggestions.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
            {draft.map((spec, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  value={spec.label}
                  onChange={(ev) =>
                    setDraft((d) => d.map((s, j) => (j === i ? { ...s, label: ev.target.value } : s)))
                  }
                  placeholder="Name, e.g. Voltage"
                  aria-label={`Spec ${i + 1} name`}
                  list={datalistId}
                  maxLength={80}
                  className="h-9 flex-1"
                />
                <Input
                  value={spec.value}
                  onChange={(ev) =>
                    setDraft((d) => d.map((s, j) => (j === i ? { ...s, value: ev.target.value } : s)))
                  }
                  placeholder="Value, e.g. 480 V"
                  aria-label={`Spec ${i + 1} value`}
                  maxLength={200}
                  className="h-9 flex-1"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 shrink-0 text-muted-foreground"
                  aria-label={`Remove spec ${i + 1}`}
                  onClick={() => setDraft((d) => d.filter((_, j) => j !== i))}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setDraft((d) => [...d, { label: '', value: '' }])}
              >
                <Plus className="h-4 w-4" />
                Add row
              </Button>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditing(false)}
                  disabled={update.isPending}
                >
                  Cancel
                </Button>
                <Button type="button" size="sm" onClick={saveSpecs} disabled={update.isPending}>
                  {update.isPending ? 'Saving…' : 'Save specs'}
                </Button>
              </div>
            </div>
          </div>
        ) : equipment.specs.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No specs recorded.
            {suggestions.length > 0 && ` Useful ones here: ${suggestions.slice(0, 4).join(', ')}.`}
          </p>
        ) : (
          <dl className="divide-y divide-border text-sm">
            {equipment.specs.map((s, i) => (
              <div key={`${s.label}-${i}`} className="flex items-baseline justify-between gap-3 py-2">
                <dt className="text-muted-foreground">{s.label}</dt>
                <dd className="text-right font-medium text-foreground">{s.value || '—'}</dd>
              </div>
            ))}
          </dl>
        )}
      </CardContent>
    </Card>
  );
}

function NotesCard({ equipment, canEdit }: { equipment: Equipment; canEdit: boolean }) {
  const update = useUpdateEquipment();
  const save = async (patch: EquipmentPatch) => {
    await update.mutateAsync({ id: equipment.id, patch });
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>Safety & notes</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div className="space-y-1">
          <span className="text-muted-foreground">Safety requirements</span>
          <EditableText
            value={equipment.safety_notes ?? ''}
            multiline
            rows={4}
            maxLength={4000}
            ariaLabel="Safety requirements"
            canEdit={canEdit}
            density="kv"
            placeholder="PPE, lockout / tagout steps, training or sign-off required to operate."
            onSave={(next) => save({ safety_notes: next })}
          />
        </div>
        <div className="space-y-1">
          <span className="text-muted-foreground">Notes</span>
          <EditableText
            value={equipment.notes ?? ''}
            multiline
            rows={4}
            maxLength={4000}
            ariaLabel="Notes"
            canEdit={canEdit}
            density="kv"
            placeholder="Anything else worth knowing."
            onSave={(next) => save({ notes: next })}
          />
        </div>
      </CardContent>
    </Card>
  );
}